// react-hook-form v7's `getDirtyFields()` builds a structural mirror of
// `defaultValues` inside `formState.dirtyFields`. For every array or object
// field it creates a matching container (`[]` or `{}`), and only the *leaves*
// carry a boolean (`true` = changed, `false` = unchanged).
//
// Problem: `getFieldState(name).isDirty` does `!!dirtyFields[name]`, but
// empty arrays and objects are *truthy* in JavaScript:
//
//   defaultValues: { tags: [] }        → dirtyFields.tags = []          → !![] = true  (wrong)
//   defaultValues: { tags: ['a'] }     → dirtyFields.tags = [false]     → !![false] = true  (wrong)
//   defaultValues: { item: {x: 'a'} }  → dirtyFields.item = {x: false}  → !!{…} = true  (wrong)
//
// All three are false positives — the values haven't changed from defaults.
//
// This helper recursively walks the dirtyFields tree and returns `true` only
// when at least one leaf is literally `true`.
//
// It is consistent with how RHF computes dirtyFields from defaultValues,
// regardless of whether the default is empty or pre-populated:
//
//   defaultValues: { policies: [] }
//     value []          → dirtyFields.policies = []               → hasDirtyLeaf = false  (unchanged)
//     value ['p1']      → dirtyFields.policies = [true]           → hasDirtyLeaf = true   (added)
//
//   defaultValues: { roles: ['ADMIN'] }
//     value ['ADMIN']        → dirtyFields.roles = [false]        → hasDirtyLeaf = false  (unchanged)
//     value ['VIEWER']       → dirtyFields.roles = [true]         → hasDirtyLeaf = true   (replaced)
//     value []               → dirtyFields.roles = [true]         → hasDirtyLeaf = true   (removed)
//     value ['ADMIN','VIEW'] → dirtyFields.roles = [false,true]   → hasDirtyLeaf = true   (appended)
//
//   defaultValues: { items: [{id: 1, name: 'a'}] }
//     value [{id:1,name:'a'}] → dirtyFields = [{id:false,name:false}] → hasDirtyLeaf = false
//     value [{id:1,name:'b'}] → dirtyFields = [{id:false,name:true}]  → hasDirtyLeaf = true
//
//   defaultValues: { title: 'hello' }
//     value 'hello' → dirtyFields.title = false → hasDirtyLeaf = false  (unchanged)
//     value 'world' → dirtyFields.title = true  → hasDirtyLeaf = true   (changed)
//
export const hasDirtyLeaf = (value: unknown): boolean => {
  if (value === true) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.some(hasDirtyLeaf);
  }
  if (typeof value === 'object' && value !== null) {
    return Object.values(value).some(hasDirtyLeaf);
  }
  return false;
};
